import React, { useState, useEffect, useRef } from 'react';
import { Table, Row, Col, Breadcrumb, Button, Typography, List, Space, Grid, Tooltip } from 'antd';
import { CSVLink } from 'react-csv';
import { ADDITIONAL_COL_1 as ADDITIONAL_COL } from '../../../config';
import {
  intialPaginationState,
  showingPageTotalRange,
  filter,
  heightWidth,
  onlyYears,
  stampSize,
  unexpectedError,
} from '../../../common_functions';
import { DownloadOutlined, ReloadOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import AdvanceFilter from './AdvanceFilter';
import CollectionLocationsForm from '../common-comp/CollectionLocationsForm';
import AdditionalColModal from './AdditionalColModal';

const defaultCheckedList = ADDITIONAL_COL.filter(col => col.checked).map(col => col.key);

const DataReport = props => {
  const collectionFormRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [sorter, setSorter] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [pagination, setPagination] = useState(intialPaginationState);
  const [loading, setLoading] = useState(false);
  const [checkedList, setCheckedList] = useState(defaultCheckedList);
  const [tempCheckedList, setTempCheckedList] = useState(defaultCheckedList);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [resetFilterData, setResetFilterData] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isResetting, setIsResetting] = useState(false);
  const [isModal2Visible, setModal2Visible] = useState(false);
  const [deviations, setDeviations] = useState(null);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getDataReports(intialPaginationState);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchDeviations = async () => {
      try {
        const response = await props.getItems('configuration', {
          fields: ['name', 'value', 'type'],
          filters: { type: { _eq: 'xrf' }, name: { _icontains: 'standard deviation' } },
          limit: 1,
        });
        const value = response?.data?.[0]?.value;
        setDeviations(value != null ? Number(value) : null);
      } catch (error) {
        unexpectedError(error);
      }
    };
    fetchDeviations();
  }, []);

  const transformReportData = rawData => {
    return rawData.map(item => {
      const print = item.print || {};
      const xrf = print.xrf?.[0] || {};
      const glossmeter = print.glossmeter?.[0] || {};
      const photoSpectroMeter = print.photo_spectro_meter?.[0] || {};
      const micrometer = print.micrometer?.[0] || {};
      const basicPhotos = print.basic_photos?.[0] || {};

      return {
        id: item.id,
        title: item.title,
        acquisition: item.acquisition,
        collections: item.collections?.name,
        location: item?.location?.name,
        paper_group: item?.paper_group?.name,
        authenticity_confidences: item.authenticity_confidences?.value,
        authenticity_confidence_reason: item.authenticity_confidences?.desc,
        print_common_tile: print?.object_name,
        print_date_range: onlyYears(print?.creation_date_start_year, print?.creation_date_end_year),
        negative_id: print?.negative_id?.obj_name,
        neg_date_range: onlyYears(print?.negative_id?.creation_earliest, print?.negative_id?.creation_latest),
        portfolio_info: print?.portfolio_add_number?.name
          ? `${print.portfolio_add_number.name}${print.edition_set?.edition_number ? `-${print.edition_set.edition_number}` : ''}`
          : '',
        date_confidence: print?.date_confidence || [],
        date_confidence_reason: print?.date_confidence || [],
        image_size: heightWidth(print?.image_dimension_width, print?.image_dimension_height),
        stamp_name: print?.stamp?.stamp_name,
        stamp_size: stampSize(print?.name_line_width, print?.city_line_width, print?.stamp_dimension_height),
        standard_stamp_size: stampSize(print?.standard_name_line_width, print?.standard_city_line_width, print?.standard_stamp_height),
        gloss: glossmeter?.paper_sheen,
        mount_thickness: micrometer?.thickness,
        paper_weight: micrometer?.paper_weight,
        avg_dmin_ba_sr: xrf.avg_dmin_ba_sr,
        avg_dmin_ba: xrf.avg_dmin_ba,
        avg_dmin_sr: xrf.avg_dmin_sr,
        avg_dmin_ba_sd: xrf.avg_dmin_ba_sd,
        avg_dmin_sr_sd: xrf.avg_dmin_sr_sd,
        presence_of_selenium_se: xrf.presence_of_selenium_se,
        oba: photoSpectroMeter?.oba,
        annotations_recto_handwriting: basicPhotos.annotations_recto,
        annotations_verso_handwriting: basicPhotos.annotations_verso,
        stamp_verso_handwriting: basicPhotos.stamp_verso,
      };
    });
  };

  const getDataReports = async (pagination, filters = { object_type: { _eq: 'print' } }, sorter = '') => {
    const isAdmin = props.roleType === 'admin';
    let location = collectionFormRef?.current?.getFieldValue('locations');
    let collections = collectionFormRef?.current?.getFieldValue('collections');

    if (!isAdmin) {
      filters = { ...filters, collections: { _in: props.collections_permissions.view } };
    }

    if (location) {
      filters = { ...filters, location: { _eq: location } };
    }
    if (collections) {
      filters = { ...filters, collections: { _eq: collections } };
    }
    try {
      setLoading(true);
      const response = await props.getItems('collection_obj', {
        fields: [
          'id,title, acquisition,collections.name,collections.id,print.object_name',
          'print.creation_date_start_year,print.creation_date_end_year,print.photo_spectro_meter.oba,location.name',
          'print.date_confidence.date_confidence_id.value',
          'print.date_confidence.date_confidence_id.date_confidence',
          'print.date_confidence.date_confidence_id.description',
          'print.xrf.avg_dmin_ba_sr',
          'print.xrf.avg_dmin_ba',
          'print.xrf.avg_dmin_sr',
          'print.xrf.avg_dmin_ba_sd',
          'print.xrf.avg_dmin_sr_sd',
          'print.xrf.presence_of_selenium_se',
          'print.glossmeter.paper_sheen',
          'print.micrometer.paper_weight',
          'print.micrometer.thickness',
          'paper_group.name',
          'authenticity_confidences.value',
          'authenticity_confidences.desc',
          'print.object_name',
          'print.negative_id.obj_name',
          'print.negative_id.creation_earliest',
          'print.negative_id.creation_latest',
          'print.creation_date_start_year',
          'print.creation_date_end_year',
          'print.image_dimension_height',
          'print.image_dimension_width',
          'print.portfolio_add_number.name',
          'print.edition_set.edition_number',
          'print.stamp.stamp_name',
          'print.basic_photos.annotations_verso.hand_writing_notes.name',
          'print.basic_photos.annotations_verso.hand_writing_notes.begin_date',
          'print.basic_photos.annotations_verso.hand_writing_notes.end_date',
          'print.basic_photos.annotations_recto.hand_writing_notes.name',
          'print.basic_photos.annotations_recto.hand_writing_notes.begin_date',
          'print.basic_photos.annotations_recto.hand_writing_notes.end_date',
          'print.basic_photos.stamp_verso.hand_writing_notes.name',
          'print.basic_photos.stamp_verso.hand_writing_notes.begin_date',
          'print.basic_photos.stamp_verso.hand_writing_notes.end_date',
          'print.image_dimension_width,print.image_dimension_height,print.stamp.width, print.stamp.height',
          'print.standard_stamp_height,print.standard_name_line_width,print.standard_city_line_width',
          'print.city_line_width',
          'print.name_line_width',
          'print.stamp_dimension_width',
          'print.stamp_dimension_height',
        ],
        filters: filters,
        sort: sorter?.field ? `${sorter.order === 'ascend' ? '' : '-'}${sorter.field}` : '-id',
        offset: pagination.pageSize * (pagination.current - 1),
        limit: pagination.pageSize,
        meta: 'filter_count',
      });

      setReports(transformReportData(response.data));
      pagination.total = response.meta.filter_count;
      setPagination(pagination);
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const getBaSrRange = record => {
    const ba = parseFloat(record.avg_dmin_ba);
    const sr = parseFloat(record.avg_dmin_sr);
    const baSd = parseFloat(record.avg_dmin_ba_sd) || 0;
    const srSd = parseFloat(record.avg_dmin_sr_sd) || 0;

    if (!Number.isFinite(ba) || !Number.isFinite(sr) || deviations == null) {
      return { low: null, high: null };
    }

    const lowDenominator = sr + srSd * deviations;
    const highDenominator = sr - srSd * deviations;

    return {
      low: lowDenominator ? (ba - baSd * deviations) / lowDenominator : null,
      high: highDenominator ? (ba + baSd * deviations) / highDenominator : null,
    };
  };

  const rowSelection = {
    selectedRowKeys: selectedRows.map(row => row.id),
    preserveSelectedRowKeys: true,
    onChange: (_, rows) => {
      setSelectedRows(rows);
    },
  };

  // Long text (a genuine reason sentence, or garbled placeholder data) gets clipped to one line
  // with "…" and the full content on hover, instead of stretching the row tall or wrapping raw.
  const TruncatedText = ({ text, maxWidth = 200, threshold = 28 }) => {
    if (!text) return <span style={{ color: '#bfbfbf' }}>—</span>;
    if (text.length <= threshold) {
      return <span style={{ color: '#595959' }}>{text}</span>;
    }
    return (
      <Tooltip title={text} placement="topLeft">
        <span
          style={{
            display: 'inline-block',
            maxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom',
            color: '#595959',
            cursor: 'help',
          }}
        >
          {text}
        </span>
      </Tooltip>
    );
  };

  // Confidence "value" is normally a short score (e.g. "99"), which reads well as a small pill.
  // Some records carry long placeholder/garbage text in that same field — badging that would
  // wrap into an ugly multi-line box, so fall back to truncated text + tooltip past a short length.
  const ValueBadge = ({ value }) => {
    if (value == null || value === '') return <span style={{ color: '#bfbfbf' }}>—</span>;
    if (String(value).length > 8) {
      return <TruncatedText text={String(value)} maxWidth={140} threshold={0} />;
    }
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: 12,
          background: '#f7f7e8',
          border: '1px solid #d9d68f',
          color: '#7a7800',
          fontWeight: 600,
          fontSize: 13,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    );
  };

  const DEFAULT_COLUMNS = [
    {
      title: 'Acquisition',
      dataIndex: 'acquisition',
      key: 'acquisition',
      width: 150,
      ellipsis: true,
      ...filter('acquisition'),
    },
    {
      title: 'Collection',
      dataIndex: 'collections',
      key: 'collections',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 200,
      ellipsis: true,
    },
  ];

  const allColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'id' ? sortedInfo.order : null,
      ellipsis: true,
    },
    ...DEFAULT_COLUMNS,
    {
      title: 'Print Common Title',
      dataIndex: 'print_common_tile',
      key: 'print_common_tile',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Print Date Range',
      dataIndex: 'print_date_range',
      key: 'print_date_range',
    },
    {
      title: 'Negative/image',
      dataIndex: 'negative_id',
      key: 'negative_id',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Negative Date',
      dataIndex: 'neg_date_range',
      key: 'neg_date_range',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Portfolio',
      dataIndex: 'portfolio_info',
      key: 'portfolio_info',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Authenticity Value',
      dataIndex: 'authenticity_confidences',
      key: 'authenticity_confidences',
      width: 150,
      render: value => <ValueBadge value={value} />,
    },
    {
      title: 'Authenticity Confidence Reason',
      dataIndex: 'authenticity_confidence_reason',
      key: 'authenticity_confidence_reason',
      width: 220,
      render: text => <TruncatedText text={text} maxWidth={200} />,
    },
    {
      title: 'Date Confidence Value',
      dataIndex: 'date_confidence',
      key: 'date_confidence',
      width: 220,
      render: dateConfidence =>
        dateConfidence?.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dateConfidence.map((item, idx) => (
              <div key={idx} style={{ borderLeft: '3px solid #a6a300', paddingLeft: 8 }}>
                <strong style={{ color: '#212329', marginRight: 6 }}>{item?.date_confidence_id?.date_confidence}</strong>
                <ValueBadge value={item?.date_confidence_id?.value} />
              </div>
            ))}
          </div>
        ) : (
          <span style={{ color: '#bfbfbf' }}>—</span>
        ),
    },
    {
      title: 'Date Confidence Reason',
      dataIndex: 'date_confidence_reason',
      key: 'date_confidence_reason',
      render: dateConfidence =>
        dateConfidence?.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dateConfidence.map((item, idx) => (
              <div key={idx} style={{ borderLeft: '3px solid #a6a300', paddingLeft: 8 }}>
                <strong style={{ color: '#212329', display: 'block' }}>{item?.date_confidence_id?.date_confidence}</strong>
                <TruncatedText text={item?.date_confidence_id?.description} maxWidth={190} />
              </div>
            ))}
          </div>
        ) : (
          <span style={{ color: '#bfbfbf' }}>—</span>
        ),
    },
    {
      title: 'Ba absolute',
      dataIndex: 'avg_dmin_ba',
      key: 'avg_dmin_ba',
    },
    {
      title: 'Sr absolute',
      dataIndex: 'avg_dmin_sr',
      key: 'avg_dmin_sr',
    },
    {
      title: 'Ba/Sr',
      dataIndex: 'avg_dmin_ba_sr',
      key: 'avg_dmin_ba_sr',
    },
    {
      title: 'Ba/Sr Range (Low)',
      dataIndex: 'avg_dmin_ba_sr_range_low',
      key: 'avg_dmin_ba_sr_range_low',
      checkboxKey: 'avg_dmin_ba_sr_range',
      render: (_, record) => {
        const { low } = getBaSrRange(record);
        return low != null ? low.toFixed(2) : '';
      },
    },
    {
      title: 'Ba/Sr Range (High)',
      dataIndex: 'avg_dmin_ba_sr_range_high',
      key: 'avg_dmin_ba_sr_range_high',
      checkboxKey: 'avg_dmin_ba_sr_range',
      render: (_, record) => {
        const { high } = getBaSrRange(record);
        return high != null ? high.toFixed(2) : '';
      },
    },
    {
      title: 'Selenium yes/no',
      dataIndex: 'presence_of_selenium_se',
      key: 'presence_of_selenium_se',
    },
    {
      title: 'Gloss',
      dataIndex: 'paper_sheen',
      key: 'paper_sheen',
    },
    {
      title: 'OBA',
      dataIndex: 'oba',
      key: 'oba',
    },
    {
      title: 'Annotations Recto Handwriting',
      dataIndex: 'annotations_recto_handwriting',
      key: 'annotations_recto_handwriting',
      render: annotationRecto => {
        return (
          <>
            <List
              dataSource={annotationRecto}
              renderItem={item =>
                item?.hand_writing_notes && (
                  <List.Item style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px', borderBottom: 'none' }}>
                    <strong>{item?.hand_writing_notes?.name} : </strong>
                    {onlyYears(item?.hand_writing_notes?.begin_date, item?.hand_writing_notes?.end_date)}
                  </List.Item>
                )
              }
            />
          </>
        );
      },
    },
    {
      title: 'Annotations Verso Handwriting',
      dataIndex: 'annotations_verso_handwriting',
      key: 'annotations_verso_handwriting',
      render: annotationVerso => {
        return (
          <>
            <List
              dataSource={annotationVerso}
              renderItem={item =>
                item?.hand_writing_notes && (
                  <List.Item style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px', borderBottom: 'none' }}>
                    <strong>{item?.hand_writing_notes?.name} : </strong>
                    {onlyYears(item?.hand_writing_notes?.begin_date, item?.hand_writing_notes?.end_date)}
                  </List.Item>
                )
              }
            />
          </>
        );
      },
    },
    {
      title: 'Stamp Verso Handwriting',
      dataIndex: 'stamp_verso_handwriting',
      key: 'stamp_verso_handwriting',
      render: stampVerso => {
        return (
          <>
            <List
              dataSource={stampVerso}
              renderItem={item =>
                item?.hand_writing_notes && (
                  <List.Item style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px', borderBottom: 'none' }}>
                    <strong>{item?.hand_writing_notes?.name} : </strong>
                    {onlyYears(item?.hand_writing_notes?.begin_date, item?.hand_writing_notes?.end_date)}
                  </List.Item>
                )
              }
            />
          </>
        );
      },
    },
    {
      title: 'Image Size (w X h)',
      dataIndex: 'image_size',
      key: 'image_size',
    },
    {
      title: 'Stamp Name',
      dataIndex: 'stamp_name',
      key: 'stamp_name',
    },
    {
      title: 'Stamp Size (Name X City X Height)',
      dataIndex: 'stamp_size',
      key: 'stamp_size',
    },
    {
      title: 'Standard Stamp Size (Name X City X Height)',
      dataIndex: 'standard_stamp_size',
      key: 'standard_stamp_size',
    },
    {
      title: 'Paper Group',
      dataIndex: 'paper_group',
      key: 'paper_group',
    },
    {
      title: 'Paper Weight',
      dataIndex: 'paper_weight',
      key: 'paper_weight',
    },
    {
      title: 'Mount Thickness',
      dataIndex: 'mount_thickness',
      key: 'mount_thickness',
    },
  ];

  const newColumns = allColumns
    .filter(col => DEFAULT_COLUMNS.some(defaultCol => defaultCol.key === col.key) || checkedList.includes(col.checkboxKey || col.key))
    .map(col => ({ width: 150, ...col }));

  const options = ADDITIONAL_COL.map(({ key, value }) => ({
    label: value,
    value: key,
  }));

  const csvHeaders = newColumns.map(col => ({ label: col.title, key: col.dataIndex }));

  const handleClose = () => {
    setIsModalVisible(false);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const showModal_2 = () => {
    setTempCheckedList(checkedList);
    setModal2Visible(true);
  };
  const formatHandwriting = items =>
    items
      ?.map(item =>
        item?.hand_writing_notes
          ? `${item.hand_writing_notes.name}: ${onlyYears(item.hand_writing_notes.begin_date, item.hand_writing_notes.end_date)}`
          : ''
      )
      .filter(Boolean)
      .join('\n');

  const formatDateConfidence = items =>
    items
      ?.map(item => (item?.date_confidence_id ? `${item.date_confidence_id.date_confidence}: ${item.date_confidence_id.value}` : ''))
      .filter(Boolean)
      .join('\n');

  const formatDateConfidenceReason = items =>
    items
      ?.map(item =>
        item?.date_confidence_id ? `${item.date_confidence_id.date_confidence}: ${item.date_confidence_id.description}` : ''
      )
      .filter(Boolean)
      .join('\n');

  const csvData = selectedRows.map(row => {
    const formattedRow = {};

    newColumns.forEach(col => {
      let value = row[col.dataIndex];

      if (col.render) {
        value = col.render(null, row);
      }

      switch (col.dataIndex) {
        case 'authenticity_confidences':
        case 'authenticity_confidence_reason':
          value = row[col.dataIndex] ?? '';
          break;

        case 'date_confidence':
          value = formatDateConfidence(row[col.dataIndex]);
          break;

        case 'date_confidence_reason':
          value = formatDateConfidenceReason(row[col.dataIndex]);
          break;

        case 'stamp_verso_handwriting':
        case 'annotations_verso_handwriting':
        case 'annotations_recto_handwriting':
          value = formatHandwriting(row[col.dataIndex]);
          break;

        default:
          value = value ?? '';
      }

      formattedRow[col.dataIndex] = value;
    });

    return formattedRow;
  });

  const handleTableChange = async (pagination, filters, sorter = '') => {
    sorter = sorter?.order == null ? '' : sorter;
    setPagination(pagination);
    setSorter(sorter);
    let filtersitems = { object_type: { _eq: 'print' } };
    for (let key in filters) {
      if (filters.hasOwnProperty(key)) {
        let val = filters[key];
        if (val) {
          filtersitems[key] = { _icontains: val[0] };
        }
      }
    }
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    await getDataReports(pagination, filtersitems, sorter);
  };

  useEffect(() => {
    const checkReset = async () => {
      if (isResetting && reports.length === 0) {
        await getDataReports(intialPaginationState);
        setIsResetting(false);
      }
    };
    checkReset();
  }, [reports, isResetting]);

  const onFinish = () => {
    setReports([]);
    setPagination(intialPaginationState);
    setIsResetting(true);
  };

  return (
    <Row className={!!loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Analytical", href: "/reports/analytical-report/data-report" }, { title: "Data Report" }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <div className="dashboard">
          <Row gutter={[24, 12]} className="page-header-section report-buttons-row" align="middle">
            <Col xs={24} md={6}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Data Report Result
              </Typography.Title>
            </Col>
            <Col xs={24} md={18} style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <Space wrap size={[8, 8]}>
                <Button icon={<PlusOutlined />} type="primary" size="small" onClick={showModal_2}>
                  Add Columns
                </Button>
                <Button icon={<SearchOutlined />} type="primary" size="small" onClick={showModal}>
                  Advanced Data Search
                </Button>

                <Button
                  size="small"
                  danger
                  onClick={async () => {
                    collectionFormRef.current.resetFields();
                    await onFinish();
                    setResetFilterData(true);
                  }}
                  icon={<ReloadOutlined />}
                >
                  Reset Filters
                </Button>

                <CSVLink data={csvData} headers={csvHeaders} filename="analytical-data-report.csv">
                  <Button type="primary" disabled={selectedRows.length === 0} icon={<DownloadOutlined />} size="small">
                    Export CSV
                  </Button>
                </CSVLink>
              </Space>
            </Col>
          </Row>

          <CollectionLocationsForm props={props} onFinish={onFinish} formRef={collectionFormRef} resetFilterData={resetFilterData} />

          <div className="artist-list data-report-table" style={{ marginTop: '20px' }}>
            <Table
              className="no-fixed-last-col"
              columns={newColumns}
              rowSelection={rowSelection}
              scroll={{ x: 'max-content' }}
              sticky
              size={isMobile ? 'small' : 'middle'}
              bordered
              rowKey={record => record.id}
              dataSource={reports}
              rowClassName={(_, index) => (index % 2 === 1 ? 'data-report-row-alt' : '')}
              pagination={
                reports.length > 0
                  ? { ...pagination, ...showingPageTotalRange, simple: isMobile, showSizeChanger: !isMobile }
                  : false
              }
              loading={loading}
              onChange={handleTableChange}
            />
            <style>{`
              .data-report-table .ant-table-tbody > tr.data-report-row-alt > td { background: #faf9f2 !important; }
              .data-report-table .ant-table-thead > tr > th { border-bottom: 2px solid #a6a300; }
              .data-report-table .ant-table-tbody > tr:hover > td { background: #f7f7e8 !important; }
            `}</style>
          </div>
        </div>
        <AdvanceFilter
          visible={isModalVisible}
          onClose={handleClose}
          props={props}
          resetDataState={setResetFilterData}
          resetData={resetFilterData}
          fetchReports={getDataReports}
        />
        <AdditionalColModal
          setModal2Visible={setModal2Visible}
          isModal2Visible={isModal2Visible}
          tempCheckedList={tempCheckedList}
          setTempCheckedList={setTempCheckedList}
          options={options}
          checkedList={checkedList}
          setCheckedList={setCheckedList}
          ADDITIONAL_COL={ADDITIONAL_COL}
        />
      </Col>
    </Row>
  );
};

export default DataReport;
